import { RecordStatus } from "@components/custom/dropdowns/record-status-dropdown";
import { getClassrooms, getClassroomsStats } from "@services/classroom.utils";
import { getOrganizationUsers } from "@services/user.util";
import { useContainer } from "@src/contexts/container-context";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Hook to fetch organizations for a given org code
 */
export function useOrganizations(orgCode, options = {}) {
  const { organizationService } = useContainer();

  return useQuery({
    queryKey: ["organizations", orgCode],
    queryFn: () => organizationService.getChildOrganizationStats(orgCode),
    enabled: !!orgCode,
    placeholderData: [],
    ...options,
  });
}

/**
 * Hook to fetch classrooms for a user/role/org.
 *
 * `isOrgSelected` is in the query key because it picks the endpoint: for a
 * multi-org role `true` requests one org's classrooms, `false` every child org's.
 */
export function useClassrooms(
  {
    userId,
    role,
    orgCode,
    status = RecordStatus.Active,
    isOrgSelected = false,
  },
  options = {}
) {
  const { classroomService } = useContainer();

  return useQuery({
    queryKey: ["classrooms", userId, role, orgCode, status, isOrgSelected],
    queryFn: () =>
      getClassrooms({
        classroomService,
        userId,
        role,
        orgCode,
        status,
        isOrgSelected,
      }),
    enabled: !!(userId && role && orgCode),
    placeholderData: [],
    ...options,
  });
}

export function useDashboardConfigs(options = {}) {
  const { organizationService } = useContainer();

  return useQuery({
    queryKey: ["dashboardConfigs"],
    queryFn: () => organizationService.getDashboardConfigs(),
    ...options,
  });
}

/**
 * Hook to fetch classroom stats for a user/role/org
 * This is different from useClassrooms - it only supports INSTRUCTOR and ORG_ADMIN roles
 * (no multi-org support)
 */
export function useClassroomsStats({ userId, role, orgCode }, options = {}) {
  const { classroomService } = useContainer();

  return useQuery({
    queryKey: ["classroomsStats", userId, role, orgCode],
    queryFn: () =>
      getClassroomsStats({ classroomService, userId, role, orgCode }),
    enabled: !!(userId && role && orgCode),
    ...options,
  });
}

/**
 * Hook to fetch organization users
 *
 * @param {object} scope
 * @param {string} [scope.orgCode] - The code of the organization
 * @param {string|number} [scope.role] - The role of the user _making the request_. This is used to determine the API endpoint to use.
 * @param {string} [scope.userId] - The id of the user _making the request_. This is used to determine the API endpoint to use.
 * @param {string} [scope.status] - Filter by user status: "active" (default), "archived", or "all".
 * @param {object} [options] - The options for the query
 * @returns {object} The query result
 */
export function useOrganizationUsers(
  { role, orgCode, userId, status = RecordStatus.Active },
  options = {}
) {
  const { userService } = useContainer();

  return useQuery({
    queryKey: ["organizationUsers", role, orgCode, userId, status],
    queryFn: () =>
      getOrganizationUsers({ userService, role, orgCode, userId, status }),
    enabled: !!(role && orgCode && userId),
    ...options,
  });
}

/**
 * Hook to fetch integrations/connections for an organization
 */
export function useIntegrations(orgCode, options = {}) {
  const { integrationService } = useContainer();

  return useQuery({
    queryKey: ["integrations", orgCode],
    queryFn: () => integrationService.getConnectionsList(orgCode),
    enabled: !!orgCode,
    ...options,
  });
}

/**
 * Hook to fetch child organizations for a parent organization
 */
export function useChildOrganizations(orgCode, options = {}) {
  const { organizationService } = useContainer();

  return useQuery({
    queryKey: ["childOrganizations", orgCode],
    queryFn: () => organizationService.getChildOrganizations(orgCode),
    enabled: !!orgCode,
    ...options,
  });
}

/**
 * Hook to fetch organization details by code
 */
export function useOrganizationDetails(orgCode, options = {}) {
  const { organizationService } = useContainer();

  return useQuery({
    queryKey: ["organizationDetails", orgCode],
    queryFn: () => organizationService.getOrganizationDetails(orgCode),
    enabled: !!orgCode,
    ...options,
  });
}

/**
 * Hook to update an organization's metadata.
 *
 * @param {string} orgCode - The organization code.
 * @param {object} [options={}] - Additional react-query mutation options.
 * @returns {object} The mutation result.
 */
export function useUpdateOrganization(orgCode, options = {}) {
  const { organizationService } = useContainer();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rosterSyncEnabled }) =>
      organizationService.updateOrganization(orgCode, {
        rosterSyncEnabled,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["organizationDetails", orgCode],
      }),
    ...options,
  });
}

/**
 * Hook to fetch users for an organization with specific roles.
 *
 * @param {string} orgCode - The organization code.
 * @param {Array<string|number>} roles - List of role IDs.
 * @param {object} [options={}] - Additional react-query options.
 * @returns {object} The result of the useQuery hook.
 */
export function useOrganizationUsersForRoles(orgCode, roles, options = {}) {
  const { userService } = useContainer();

  return useQuery({
    queryKey: ["organizationUsersForRoles", orgCode, roles],
    queryFn: () => userService.getOrganizationUsersForRoles(orgCode, roles),
    enabled: !!(orgCode && roles),
    ...options,
  });
}

/**
 * Hook to fetch organization device count
 */
export function useOrganizationDeviceCount(orgCode, options = {}) {
  const { organizationService } = useContainer();

  return useQuery({
    queryKey: ["organizationDeviceCount", orgCode],
    queryFn: () =>
      organizationService
        .getOrganizationDeviceCount(orgCode)
        .then((response) => response?.deviceCount || 0),
    enabled: !!orgCode,
    ...options,
  });
}

export const useOrganizationDevices = (orgCode, options = {}) => {
  const { organizationService } = useContainer();

  return useQuery({
    queryKey: ["organizationDevices", orgCode],
    queryFn: () => organizationService.getOrganizationHeadsetDevices(orgCode),
    enabled: !!orgCode,
    ...options,
  });
};
